/**
 * AB#649 — custom URL entries in a site's navigation menu.
 *
 * The server owns validation (label/url non-empty, <= 32 links, and a URL that
 * is `http(s)://`, root-relative `/path`, or `#fragment` — `javascript:` and
 * `data:` are rejected). This module mirrors those rules so the user gets an
 * inline field error instead of a toast from a 400; it is NOT the security
 * boundary.
 */
import type { NavLink } from '@wordpuppi/shared';

export const MAX_NAV_LINKS = 32;

export interface NavLinkErrors {
  label?: string;
  url?: string;
}

/** Empty object = valid. */
export function validateNavLink(input: { label: string; url: string }): NavLinkErrors {
  const errors: NavLinkErrors = {};
  const label = input.label.trim();
  const url = input.url.trim();

  if (!label) errors.label = 'Label is required';
  if (!url) {
    errors.url = 'URL is required';
    return errors;
  }

  // ponytail: prefix checks, not `new URL()` — relative forms have no base and
  // URL() would happily parse `javascript:alert(1)` as a valid absolute URL.
  const ok =
    /^https?:\/\/\S/i.test(url) ||
    // `//evil.com` is scheme-relative, not root-relative → rejected.
    (url.startsWith('/') && !url.startsWith('//')) ||
    (url.startsWith('#') && url.length > 1);

  if (!ok) {
    errors.url = 'Use https://…, a path like /about, or #section';
  }
  return errors;
}

/**
 * Rebuild the whole `nav_links` array from the displayed row order, stamping
 * each link with its absolute position in the menu (pages and the blog row take
 * the same `index + 1` numbering, so the three row types interleave correctly).
 */
export function navLinksForOrder(
  rows: readonly { type: string; link?: NavLink }[],
): NavLink[] {
  const out: NavLink[] = [];
  rows.forEach((row, index) => {
    if (row.type === 'link' && row.link) {
      out.push({ ...row.link, menu_order: index + 1 });
    }
  });
  return out;
}
